<template>
    <div class="panel-contents">
        <div class="d-flex align-items-center">
            <h3 v-if="thread">{{ threadTitle }}</h3>
            <div v-else class="d-flex">
                <div
                    class="spinner-grow spinner-grow-sm text-secondary me-2"
                    role="status"
                ></div>
                <div
                    class="spinner-grow spinner-grow-sm text-secondary me-2"
                    role="status"
                ></div>
                <div
                    class="spinner-grow spinner-grow-sm text-secondary"
                    role="status"
                ></div>
            </div>
        </div>

        <div class="thread-messages-shell">
            <div
                class="thread-messages-container"
                ref="messagesContainer"
                @scroll.passive="updateScrollState"
            >
                <!-- Each transaction has both a USER message and a PATTY message -->
                <template v-if="thread">
                    <template
                        v-for="transaction in threadResponses"
                        :key="transaction.id || transaction.hash"
                    >
                        <!-- User Message -->
                        <MessageBubble
                            :user="user"
                            align="right"
                            :created-at="transactionCreatedAt(transaction)"
                        >
                            <template #content>
                                <CitationText
                                    :message-text="
                                        transactionInput(transaction)
                                    "
                                />
                            </template>
                        </MessageBubble>

                        <!-- Patty Message -->
                        <div
                            v-if="transactionOutput(transaction)"
                            class="d-flex flex-column"
                            style="gap: var(--space-2)"
                        >
                            <!-- Patty Response Message -->
                            <MessageBubble
                                align="left"
                                static-name="Patty"
                                static-image="patty/patty-avatar.svg"
                                :created-at="transactionCreatedAt(transaction)"
                            >
                                <template #content>
                                    <PattyActivityTimeline
                                        v-if="isAuditMode"
                                        :activities="
                                            auditAnnotations(transaction)
                                                .activities
                                        "
                                        :complete="true"
                                    />
                                    <AuditAgentResponse
                                        v-if="isAuditMode"
                                        :content="
                                            transactionOutput(transaction)
                                        "
                                        :references="
                                            auditReferences(transaction)
                                        "
                                        @open-reference="openReference"
                                    />
                                    <CitationText
                                        v-else
                                        :message-text="
                                            transactionOutput(transaction)
                                        "
                                        :citations="auditCitations(transaction)"
                                        @open-pdf="openPdf"
                                    />
                                    <PattyAuditReferences
                                        v-if="isAuditMode"
                                        :references="
                                            auditReferences(transaction)
                                        "
                                        @open-reference="openReference"
                                    />
                                </template>
                            </MessageBubble>

                            <!-- Patty Response Message Actions -->
                            <div
                                class="d-flex flex-row align-items-center justify-content-end"
                                style="gap: var(--space-3); min-height: 24px"
                            >
                                <!-- Upvote Button -->
                                <button
                                    v-if="!isAuditMode"
                                    class="action-btn"
                                    @click="handleRate(transaction.id, 'GOOD')"
                                    :class="{
                                        selected: transaction.rating === 'GOOD',
                                    }"
                                >
                                    <img
                                        v-if="transaction.rating === 'GOOD'"
                                        :src="thumbsUpFilled"
                                    />
                                    <i v-else class="fi fi-thumbs-up"></i>
                                </button>

                                <!-- Downvote Button -->
                                <button
                                    v-if="!isAuditMode"
                                    class="action-btn"
                                    @click="handleRate(transaction.id, 'BAD')"
                                    :class="{
                                        selected: transaction.rating === 'BAD',
                                    }"
                                >
                                    <img
                                        v-if="transaction.rating === 'BAD'"
                                        :src="thumbsDownFilled"
                                    />
                                    <i v-else class="fi fi-thumbs-down"></i>
                                </button>

                                <button
                                    class="action-btn"
                                    @click="
                                        handleCopy(
                                            transactionOutput(transaction),
                                            transaction.id || transaction.hash
                                        )
                                    "
                                >
                                    <i
                                        v-if="
                                            copyingIds[
                                                transaction.id ||
                                                    transaction.hash
                                            ]
                                        "
                                        class="fi fi-check check-icon"
                                    ></i>
                                    <i v-else class="fi fi-copy"></i>
                                </button>
                            </div>
                        </div>
                    </template>
                </template>

                <!-- LIVE Question - User Message which is currently being processed by Patty -->
                <template v-if="fetching && pattyStore.queryText">
                    <MessageBubble
                        :user="user"
                        align="right"
                        :created-at="new Date().toISOString()"
                    >
                        <template #content>
                            <CitationText
                                :message-text="pattyStore.queryText"
                            />
                        </template>
                    </MessageBubble>

                    <div v-if="isAuditMode" class="live-response">
                        <PattyActivityTimeline
                            :activities="liveActivities"
                            :complete="streamComplete"
                        />
                        <div
                            v-if="preparingAnswer"
                            class="preparing-answer"
                            role="status"
                        >
                            <i class="fi fi-loader" aria-hidden="true"></i>
                            Patty is preparing the answer…
                        </div>
                    </div>

                    <transition>
                        <MessageBubble
                            v-if="textStream"
                            align="left"
                            static-name="Patty"
                            static-image="patty/patty-avatar.svg"
                            :created-at="new Date().toISOString()"
                        >
                            <template #content>
                                <AuditAgentResponse
                                    v-if="isAuditMode"
                                    :content="textStream"
                                    :references="liveReferences"
                                    @open-reference="openReference"
                                />
                                <CitationText
                                    v-else
                                    :message-text="textStream"
                                />
                            </template>
                        </MessageBubble>
                    </transition>
                </template>
            </div>

            <button
                v-if="showScrollToLatest"
                type="button"
                class="scroll-to-latest"
                aria-label="Scroll to latest message"
                @click="scrollToLatest"
            >
                <i class="fi fi-chevron-down" aria-hidden="true"></i>
                <span>Latest</span>
            </button>
        </div>

        <MessageField
            @submit="submitMessageToThread()"
            v-model="messageInput"
            :fetching="fetching"
        ></MessageField>

        <!-- PDF Viewer -->
        <PdfViewerSidekick
            :visible="pdfVisible"
            :current-pdf-file="currentPdfFile"
            @close-pdf="closePdf"
        />
    </div>
</template>

<script>
import MessageBubble from '@/components/patty/MessageBubble.vue';
import PdfViewerSidekick from '@/components/pdf_viewer/PdfViewerSidekick.vue';
import CitationText from '@/components/patty/CitationText.vue';
import MessageField from '@/components/fields/MessageField.vue';
import PattyActivityTimeline from '@/components/patty/PattyActivityTimeline.vue';
import PattyAuditReferences from '@/components/patty/PattyAuditReferences.vue';
import AuditAgentResponse from '@/components/patty/AuditAgentResponse.vue';
import { PATTY_MODES, usePattyStore } from '@/stores/patty';
import {
    applyActivityEvent,
    createAuditAgentApi,
    normalizeAuditAnnotations,
    splitReferences,
    upsertAuditReference,
} from '@/components/patty/auditAgent.js';

import thumbsUpFilled from '@/assets/images/icons/thumbs-up-filled.svg';
import thumbsDownFilled from '@/assets/images/icons/thumbs-down-filled.svg';

export default {
    name: 'PattyThread',
    components: {
        MessageBubble,
        PdfViewerSidekick,
        CitationText,
        MessageField,
        PattyActivityTimeline,
        PattyAuditReferences,
        AuditAgentResponse,
    },
    emits: ['open-reference', 'close-reference'],
    props: {
        threadId: {
            type: [Number, String],
            required: false,
            default: null,
        },
    },
    data() {
        return {
            pdfVisible: false,
            currentPdfFile: null,
            copyingIds: {},
            fetching: false,
            thread: null,
            pattyStore: null,

            textStream: '',
            messageInput: '',
            liveActivities: [],
            liveReferences: [],
            streamComplete: false,
            streamController: null,
            isNearBottom: true,

            thumbsUpFilled,
            thumbsDownFilled,
        };
    },

    watch: {
        'pattyStore.threadId'() {
            this.initializeThread();
        },
    },

    computed: {
        user() {
            return this.$userStore.user;
        },
        isAuditMode() {
            return this.pattyStore?.mode === PATTY_MODES.AUDIT_AGENT;
        },
        threadTitle() {
            return (
                this.thread?.predicted_subject ||
                this.thread?.predictedSubject ||
                this.thread?.subject ||
                'Audit conversation'
            );
        },
        threadResponses() {
            return Array.isArray(this.thread?.responses)
                ? this.thread.responses
                : [];
        },
        preparingAnswer() {
            return (
                this.fetching &&
                !this.textStream &&
                !this.liveActivities.some(
                    (activity) => activity.status === 'active'
                )
            );
        },
        showScrollToLatest() {
            return (
                !this.isNearBottom &&
                (this.threadResponses.length > 0 || this.fetching)
            );
        },
    },

    methods: {
        initializeThread() {
            if (this.isAuditMode && this.pattyStore.threadId) this.getThread();
            else if (this.pattyStore.threadId > 0) this.getThread();
            else this.createThread();
        },
        openPdf(fileHash) {
            this.$emit('close-reference');
            this.pdfVisible = true;
            this.currentPdfFile = fileHash;
        },
        closePdf() {
            this.pdfVisible = false;
            this.currentPdfFile = null;
        },
        openReference(reference) {
            this.closePdf();
            this.$emit('open-reference', reference);
        },
        transactionInput(transaction) {
            return transaction.input_text || transaction.inputText || '';
        },
        transactionOutput(transaction) {
            return transaction.output_text || transaction.outputText || '';
        },
        transactionCreatedAt(transaction) {
            return transaction.created_at || transaction.createdAt;
        },
        auditAnnotations(transaction) {
            return normalizeAuditAnnotations(transaction.annotations);
        },
        auditCitations(transaction) {
            if (!this.isAuditMode) return transaction.annotations || [];
            return splitReferences(
                this.auditAnnotations(transaction).references
            ).fileCitations;
        },
        auditReferences(transaction) {
            return splitReferences(
                this.auditAnnotations(transaction).references
            ).appReferences;
        },
        handleRate(transactionId, status) {
            const payload = {
                id: transactionId,
                rating: status,
            };

            this.$postData(
                `openai/assistant-thread/message/rate`,
                payload
            ).then((response) => {
                if (response.success) {
                    this.thread.responses.forEach((transaction) => {
                        if (transaction.id === transactionId)
                            transaction.rating = status;
                    });
                } else {
                    this.$alertStore.pushError(
                        this.$t('patty.alerts.rateMessageError')
                    );
                }
            });
        },
        handleCopy(message, messageId) {
            navigator.clipboard.writeText(message);
            this.copyingIds[messageId] = true;
            setTimeout(() => {
                this.copyingIds[messageId] = false;
            }, 1000);
        },
        updateScrollState() {
            const container = this.$refs.messagesContainer;
            if (!container) return;
            const distanceFromBottom =
                container.scrollHeight -
                container.scrollTop -
                container.clientHeight;
            this.isNearBottom = distanceFromBottom <= 32;
        },

        scrollToBottom(keepOnly = false) {
            if (keepOnly && !this.isNearBottom) return;
            this.$nextTick(() => {
                const container = this.$refs.messagesContainer;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                    this.updateScrollState();
                }
            });
        },
        scrollToLatest() {
            const container = this.$refs.messagesContainer;
            if (!container) return;
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth',
            });
        },

        submitMessageToThread() {
            if (this.fetching) return;

            if (this.isAuditMode) {
                this.submitAuditMessage();
                return;
            }

            this.textStream = '';
            this.fetching = true;

            const payload = {
                thread_id: this.thread.id,
                message: this.messageInput,
            };

            this.pattyStore.setQueryText(this.messageInput);
            this.messageInput = '';

            this.$nextTick(() => {
                this.scrollToBottom();
            });

            this.$postData(
                'openai/assistant-thread/message/create',
                payload,
                true,
                (chunk) => {
                    if (chunk.event == 'response-text') {
                        this.textStream += chunk.data;
                        this.scrollToBottom(true);
                    } else if (chunk.event == 'new-response') {
                        try {
                            const response = JSON.parse(chunk.data);
                            this.thread.responses.push(response);
                        } catch {
                            this.$alertStore.pushError(
                                this.$t('patty.alerts.parseResponseError')
                            );
                        }
                    }
                }
            ).then((response) => {
                this.fetching = false;
                if (response?.message) {
                    this.$alertStore.pushError(response.message);
                } else {
                    this.scrollToBottom(true);
                }
            });
        },

        createThread() {
            if (this.isAuditMode) {
                this.submitAuditMessage();
                return;
            }
            this.textStream = '';
            this.fetching = true;

            const payload = {
                message: this.pattyStore.queryText,
            };

            this.$nextTick(() => {
                this.scrollToBottom();
            });

            this.$postData(
                'openai/assistant-thread/create',
                payload,
                true,
                (chunk) => {
                    if (chunk.event == 'response-text') {
                        this.textStream += chunk.data;
                        this.scrollToBottom(true);
                    } else if (chunk.event == 'new-thread') {
                        try {
                            this.thread = JSON.parse(chunk.data);
                        } catch {
                            this.$alertStore.pushError(
                                this.$t('patty.alerts.parseResponseError')
                            );
                        }
                    }
                }
            ).then((response) => {
                this.fetching = false;

                if (response?.message) {
                    this.$alertStore.pushError(response.message);
                } else {
                    this.pattyStore.setQueryText('');
                    this.scrollToBottom(true);
                }
            });
        },

        getThread() {
            if (this.isAuditMode) {
                this.getAuditThread();
                return;
            }
            const threadId = this.pattyStore.threadId;
            this.fetching = true;

            const payload = {
                where: { id: threadId },
                include: { responses: true },
            };

            this.$getData('openai/assistant-thread/read', payload).then(
                (response) => {
                    if (response.success) {
                        this.thread = response.data.length
                            ? response.data[0]
                            : null;
                    } else if ('message' in response) {
                        this.$alertStore.pushError(response.message);
                    }

                    this.fetching = false;

                    this.$nextTick(() => {
                        this.scrollToBottom();
                    });
                }
            );
        },
        parseAuditEvent(event) {
            let data;
            try {
                data = JSON.parse(event.data);
            } catch {
                throw new Error(
                    'Patty returned an invalid streaming response.'
                );
            }
            if (event.event === 'response-text') {
                this.textStream += data.delta || '';
                this.scrollToBottom(true);
            } else if (event.event === 'activity') {
                const state = applyActivityEvent(this.liveActivities, data);
                this.liveActivities = state.activities;
                this.scrollToBottom(true);
            } else if (event.event === 'reference') {
                this.liveReferences = upsertAuditReference(
                    this.liveReferences,
                    data
                );
            } else if (event.event === 'new-thread') {
                this.thread = data;
            } else if (event.event === 'new-response') {
                if (!this.thread) return;
                if (!Array.isArray(this.thread.responses))
                    this.thread.responses = [];
                this.thread.responses.push(data);
            } else if (event.event === 'complete') {
                this.streamComplete = true;
            }
        },
        async submitAuditMessage() {
            const message = (
                this.messageInput || this.pattyStore.queryText
            ).trim();
            if (!message || this.fetching) return;
            const threadHash =
                this.thread?.hash || this.thread?.threadHash || null;
            this.textStream = '';
            this.liveActivities = [];
            this.liveReferences = [];
            this.streamComplete = false;
            this.fetching = true;
            this.pattyStore.setQueryText(message);
            this.messageInput = '';
            this.streamController?.abort();
            this.streamController = new AbortController();
            this.$nextTick(() => this.scrollToBottom());
            try {
                await createAuditAgentApi({
                    apiRoot: this.$env.apiRoot,
                }).streamMessage({
                    threadHash,
                    message,
                    context: this.pattyStore.context,
                    signal: this.streamController.signal,
                    onEvent: this.parseAuditEvent,
                });
                if (!this.streamComplete) {
                    throw new Error(
                        'Patty’s response was interrupted before it could be saved. Please try again.'
                    );
                }
                this.pattyStore.setQueryText('');
            } catch (error) {
                if (error?.name !== 'AbortError') {
                    this.$alertStore.pushError(
                        error?.message ||
                            'Patty could not complete the response.'
                    );
                }
            } finally {
                this.fetching = false;
                this.streamController = null;
                this.scrollToBottom(true);
            }
        },
        async getAuditThread() {
            this.fetching = true;
            this.textStream = '';
            try {
                this.thread = await createAuditAgentApi({
                    apiRoot: this.$env.apiRoot,
                }).getThread(this.pattyStore.threadId);
            } catch (error) {
                this.thread = null;
                this.$alertStore.pushError(
                    error?.message || 'The conversation could not be loaded.'
                );
            } finally {
                this.fetching = false;
                this.$nextTick(() => this.scrollToBottom());
            }
        },
    },

    created() {
        this.pattyStore = usePattyStore();
    },
    mounted() {
        if (!this.fetching) this.initializeThread();
    },
    beforeUnmount() {
        this.streamController?.abort();
    },
};
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
    transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}

.panel-contents {
    display: inline-flex;
    flex-direction: column;
    gap: var(--space-6);
    height: 100%;
    width: 100%;
}

.thread-messages-container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    overflow-y: auto;
    padding-right: 0.5rem;
}

.thread-messages-shell {
    position: relative;
    min-height: 0;
    flex: 1 1 auto;
}

.scroll-to-latest {
    position: absolute;
    z-index: 2;
    bottom: var(--space-3);
    left: 50%;
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--primary-200);
    border-radius: 999px;
    background: var(--primary-500);
    box-shadow: 0 4px 14px rgba(45, 45, 45, 0.16);
    color: var(--white);
    font-size: 0.8125rem;
    font-weight: 700;
    transform: translateX(-50%);
    cursor: pointer;
}

.scroll-to-latest:hover,
.scroll-to-latest:focus-visible {
    background: var(--primary-400);
    outline: 2px solid var(--primary-100);
    outline-offset: 2px;
}

.thread-messages-container::-webkit-scrollbar {
    width: 3px;
    opacity: 0;
    transition: all 0.3s ease;
}

.thread-messages-container:hover::-webkit-scrollbar {
    opacity: 1;
}

.thread-messages-container::-webkit-scrollbar-track {
    background: var(--neutral-450);
    border-radius: var(--radius-sm);
}

.thread-messages-container::-webkit-scrollbar-thumb {
    background: var(--neutral-600);
    border-radius: var(--radius-sm);
}

.thread-messages-container::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-700);
}

.separator {
    border-top: 1px solid var(--neutral-600);
}

.preparing-answer {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    color: var(--neutral-700);
    font-size: 0.875rem;
}

.preparing-answer i {
    animation: activity-spin 1s linear infinite;
}

@keyframes activity-spin {
    to {
        transform: rotate(360deg);
    }
}

.input-button {
    bottom: var(--space-2);
    right: var(--space-2);
}

.action-btn {
    background-color: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    height: 28px;
    width: 28px;
    position: relative;
    border-radius: 20%;
}

.action-btn img,
.action-btn i {
    transition: all 0.2s ease;
    width: var(--space-4);
    height: var(--space-4);
}

.action-btn:focus {
    outline: none;
}

.action-btn:hover {
    background-color: var(--primary-100);
    border: none;
    color: var(--neutral-900);
}

.selected {
    cursor: default;
    pointer-events: none;
}

.check-icon {
    animation: checkAppear 0.2s ease-in-out forwards;
}

@keyframes checkAppear {
    0% {
        transform: scale(0);
        opacity: 0;
    }
    70% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}
</style>
